import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Download, RotateCcw } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { arrayHasItems } from "../../helpers/common";
import {
    PRESET_COLUMN_IDS,
    dedupeDemandPlanningColumnIds,
    getDemandPlanningColumnLabel,
} from "../../consts/demandplanning";
import {
    CADENCE_OPTIONS,
    approvalTone,
    buildCadenceDates,
    cadenceLabel,
    compareScheduleLines,
    downloadPoScheduleCsv,
    effectiveCadenceValue,
    findScheduleAssignment,
    formatPoScheduleCurrency,
    formatPoScheduleDate,
    formatPoScheduleNumber,
    isPoScheduleApproved,
    parseIsoDate,
} from "../../lib/poSchedulePlanning";
import { usePoScheduleStore } from "../../lib/usePoScheduleStore";
import { DemandPlanningLoadingKeys, getPoScheduleTableData } from "../../slices/demandPlanningSlice";
import { DatePickerInput } from "../DatePickerInput";
import { LoaderSpinner } from "../LoaderSpinner";
import { SelectDropdown } from "../SelectDropdown";
import { TotalsPoSchedule } from "./poScheduleTableComponents/TotalsPoSchedule";
import { POScheduleInformationBadge } from "./poScheduleTableComponents/POScheduleInformationBadge";

const POScheduleTable = ({
    selectedForecastDemandPlanning,
    visibleColumnIds: visibleColumnIdsProp,
    searchTerm,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
}) => {
    const dispatch = useDispatch();
    const { poScheduleTableData, loadingKeys } = useSelector((state) => state.demandPlanning);
    const [exportSupplier, setExportSupplier] = useState("");

    const loading = loadingKeys.includes(DemandPlanningLoadingKeys.getPoScheduleTableData);

    const {
        asOfDate,
        globalScheduleDates,
        supplierOverrides,
        setAsOfDate,
        setGlobalScheduleDate,
        setSupplierFrequency,
        setSupplierScheduleDate,
        clearSupplierOverride,
        resetScheduleState,
    } = usePoScheduleStore();

    const columnIds = useMemo(() => {
        const fromPreset = dedupeDemandPlanningColumnIds(PRESET_COLUMN_IDS["po-schedule"]);
        if (visibleColumnIdsProp === undefined || visibleColumnIdsProp === null) {
            return fromPreset;
        }
        const allow = new Set(visibleColumnIdsProp);
        return fromPreset.filter((id) => allow.has(id));
    }, [visibleColumnIdsProp]);

    const sourceRows = useMemo(() => {
        const rows = poScheduleTableData?.sourceRows;
        return arrayHasItems(rows) ? rows : [];
    }, [poScheduleTableData]);

    useEffect(() => {
        const demandPlanningId = selectedForecastDemandPlanning?.id;
        const demandPlanningYear =
            selectedForecastDemandPlanning?.forecast_year ??
            selectedForecastDemandPlanning?.forecastYear;

        if (!demandPlanningId) return;

        dispatch(
            getPoScheduleTableData({
                demandPlanningId,
                demandPlanningYear,
                searchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
            })
        );
    }, [
        dispatch,
        selectedForecastDemandPlanning,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    ]);

    const scheduleLines = useMemo(() => {
        const grouped = new Map();

        sourceRows
            .filter(
                (record) =>
                    Number(record.unitsToOrder ?? 0) > 0 ||
                    Number(record.replenishmentCashRequired ?? 0) > 0
            )
            .forEach((record) => {
                const supplier = record.supplier?.trim() || "Supplier pending";
                const override = supplierOverrides[supplier];
                const frequency = override?.frequency ?? 3;
                const scheduleDates = override?.dates?.length
                    ? override.dates
                    : buildCadenceDates(3, globalScheduleDates);
                const assignment = findScheduleAssignment(record.nextPoRequiredDate, scheduleDates);
                const key = [supplier, assignment.scheduledPoDate, assignment.scheduleType].join("::");
                const units = Number(record.unitsToOrder ?? 0);
                const cash = Number(
                    record.replenishmentCashRequired ?? units * Number(record.totalBomCost ?? 0) ?? 0
                );
                const approved = isPoScheduleApproved(record.approved);

                const current = grouped.get(key);
                if (current) {
                    current.unitsToOrder += units;
                    current.cashRequired += cash;
                    current.approvedCount += approved ? 1 : 0;
                    current.pendingCount += approved ? 0 : 1;
                    current.rows.push(record);
                    current.skuCount = current.rows.length;
                    current.productCount = new Set(current.rows.map((row) => row.productName)).size;
                    current.requiredByDate =
                        current.requiredByDate <= record.nextPoRequiredDate
                            ? current.requiredByDate
                            : record.nextPoRequiredDate;
                    return;
                }

                grouped.set(key, {
                    supplier,
                    cadenceLabel: cadenceLabel(frequency),
                    frequency,
                    scheduledPoDate: assignment.scheduledPoDate,
                    requiredByDate: record.nextPoRequiredDate,
                    scheduleType: assignment.scheduleType,
                    skuCount: 1,
                    productCount: 1,
                    unitsToOrder: units,
                    cashRequired: cash,
                    approvedCount: approved ? 1 : 0,
                    pendingCount: approved ? 0 : 1,
                    rows: [record],
                });
            });

        return Array.from(grouped.values());
    }, [globalScheduleDates, sourceRows, supplierOverrides]);

    const orderedScheduleLines = useMemo(
        () => [...scheduleLines].sort((left, right) => compareScheduleLines(left, right, asOfDate)),
        [asOfDate, scheduleLines]
    );

    const summary = useMemo(() => {
        const suppliers = new Set(scheduleLines.map((line) => line.supplier));
        return {
            supplierCount: suppliers.size,
            batchCount: scheduleLines.length,
            skuCount: scheduleLines.reduce((sum, line) => sum + line.skuCount, 0),
            totalUnits: scheduleLines.reduce((sum, line) => sum + line.unitsToOrder, 0),
            totalCash: scheduleLines.reduce((sum, line) => sum + line.cashRequired, 0),
            interimCount: scheduleLines.filter((line) => line.scheduleType === "Interim").length,
        };
    }, [scheduleLines]);

    const supplierSummaries = useMemo(() => {
        const grouped = new Map();

        scheduleLines.forEach((line) => {
            const override = supplierOverrides[line.supplier];
            const frequency = override?.frequency ?? 3;
            const scheduleDates = override?.dates?.length
                ? override.dates
                : buildCadenceDates(3, globalScheduleDates);
            const current = grouped.get(line.supplier);
            if (current) {
                current.batchCount += 1;
                current.skuCount += line.skuCount;
                current.totalUnits += line.unitsToOrder;
                current.totalCash += line.cashRequired;
                return;
            }

            grouped.set(line.supplier, {
                supplier: line.supplier,
                frequency,
                cadenceLabel: cadenceLabel(frequency),
                scheduleDates,
                batchCount: 1,
                skuCount: line.skuCount,
                totalUnits: line.unitsToOrder,
                totalCash: line.cashRequired,
            });
        });

        return Array.from(grouped.values()).sort(
            (left, right) =>
                right.totalCash - left.totalCash || left.supplier.localeCompare(right.supplier)
        );
    }, [globalScheduleDates, scheduleLines, supplierOverrides]);

    const scheduleAsideRef = useRef(null);
    const [scheduleAsideHeight, setScheduleAsideHeight] = useState(null);

    useLayoutEffect(() => {
        const aside = scheduleAsideRef.current;
        if (!aside) return undefined;

        const updateHeight = () => {
            if (window.innerWidth < 1280) {
                setScheduleAsideHeight(null);
                return;
            };
            setScheduleAsideHeight(aside.getBoundingClientRect().height);
        };

        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(aside);

        return () => observer.disconnect();
    }, [globalScheduleDates, supplierOverrides, supplierSummaries]);

    useEffect(() => {
        if (!supplierSummaries.length) {
            setExportSupplier("");
            return;
        }

        if (!exportSupplier || !supplierSummaries.some((summaryRow) => summaryRow.supplier === exportSupplier)) {
            setExportSupplier(supplierSummaries[0]?.supplier ?? "");
        }
    }, [exportSupplier, supplierSummaries]);

    const exportRows = useMemo(() => {
        if (!exportSupplier) return [];

        return orderedScheduleLines
            .filter((line) => line.supplier === exportSupplier)
            .flatMap((line) =>
                [...line.rows]
                    .sort((left, right) => {
                        const productOrder = String(left.productName || "").localeCompare(
                            String(right.productName || "")
                        );
                        if (productOrder !== 0) return productOrder;
                        return String(left.supplySku || "").localeCompare(String(right.supplySku || ""));
                    })
                    .map((row) => {
                        const approval = row.approved;
                        const approvalLabel =
                            approval === true || approval === "true"
                                ? "Approved"
                                : approval === false || approval === "false"
                                  ? "Not Approved"
                                  : "Pending";
                        const units = Number(row.unitsToOrder ?? 0);
                        const cash = Number(
                            row.replenishmentCashRequired ?? units * Number(row.totalBomCost ?? 0) ?? 0
                        );

                        return [
                            line.supplier,
                            row.productName,
                            row.supplySku,
                            row.category,
                            row.subCategory,
                            line.scheduleType,
                            line.scheduledPoDate,
                            row.nextPoRequiredDate,
                            units,
                            Math.round(cash),
                            approvalLabel,
                            units > 0
                                ? "Use SKU-level units to estimate component and material buys."
                                : "No open buy quantity on this row.",
                        ];
                    })
            );
    }, [exportSupplier, orderedScheduleLines]);

    const asOfTime = parseIsoDate(asOfDate)?.getTime() ?? 0;
    const nextWindowDate = useMemo(
        () =>
            orderedScheduleLines.find(
                (line) => (parseIsoDate(line.scheduledPoDate)?.getTime() ?? 0) >= asOfTime
            )?.scheduledPoDate ?? null,
        [asOfTime, orderedScheduleLines]
    );
    const nextWindowBatchCount = useMemo(
        () =>
            nextWindowDate
                ? orderedScheduleLines.filter((line) => line.scheduledPoDate === nextWindowDate).length
                : 0,
        [nextWindowDate, orderedScheduleLines]
    );
    const elapsedWindowCount = useMemo(
        () =>
            orderedScheduleLines.filter(
                (line) => (parseIsoDate(line.scheduledPoDate)?.getTime() ?? 0) < asOfTime
            ).length,
        [asOfTime, orderedScheduleLines]
    );
    const overrideCount = Object.keys(supplierOverrides).length;

    const exportSupplierOptions = useMemo(
        () =>
            supplierSummaries.map((summaryRow) => ({
                label: summaryRow.supplier,
                value: summaryRow.supplier,
            })),
        [supplierSummaries]
    );

    const totals = useMemo(
        () => [
            { label: "Suppliers", displayValue: formatPoScheduleNumber(summary.supplierCount) },
            { label: "Scheduled batches", displayValue: formatPoScheduleNumber(summary.batchCount) },
            { label: "SKUs in schedule", displayValue: formatPoScheduleNumber(summary.skuCount) },
            { label: "Units to buy", displayValue: formatPoScheduleNumber(summary.totalUnits) },
            { label: "Cash required", value: summary.totalCash },
            { label: "Interim buys", displayValue: formatPoScheduleNumber(summary.interimCount) },
        ],
        [summary]
    );

    const renderScheduleCell = (columnId, line) => {
        switch (columnId) {
            case "scheduledPoDate": {
                const isNextWindow = Boolean(nextWindowDate) && line.scheduledPoDate === nextWindowDate;
                return (
                    <div className="flex flex-col gap-1">
                        <span>{formatPoScheduleDate(line.scheduledPoDate)}</span>
                        {isNextWindow ? (
                            <span className="w-fit rounded-md border-transparent whitespace-nowrap bg-slate-950 px-2 py-0.5 text-[10px] font-medium text-white">
                                Next window
                            </span>
                        ) : null}
                    </div>
                );
            }
            case "supplier":
                return (
                    <div>
                        <p className="font-medium text-primary">{line.supplier}</p>
                        <p className="mt-1 text-xs text-gray-600 whitespace-break-spaces">
                            {line.rows
                                .map((row) => row.supplySku)
                                .slice(0, 3)
                                .join(", ")}
                            {line.rows.length > 3 ? ` +${line.rows.length - 3} more` : ""}
                        </p>
                    </div>
                );
            case "cadenceLabel":
                return line.cadenceLabel;
            case "scheduleType":
                return (
                    <span
                        className={`rounded-md px-2 py-1 text-[11px] font-medium ${
                            line.scheduleType === "Planned"
                                ? "border-transparent bg-emerald-50 text-emerald-700"
                                : "border-transparent bg-amber-50 text-amber-700"
                        }`}
                    >
                        {line.scheduleType}
                    </span>
                );
            case "requiredByDate":
                return formatPoScheduleDate(line.requiredByDate);
            case "skuCount":
                return formatPoScheduleNumber(line.skuCount);
            case "productCount":
                return formatPoScheduleNumber(line.productCount);
            case "unitsToOrder":
                return formatPoScheduleNumber(line.unitsToOrder);
            case "cashRequired":
                return formatPoScheduleCurrency(line.cashRequired);
            case "approvedCount":
                return (
                    <span
                        className={`rounded-md px-2 py-1 text-[11px] whitespace-nowrap font-medium ${approvalTone(
                            line.approvedCount,
                            line.pendingCount
                        )}`}
                    >
                        {line.approvedCount} approved · {line.pendingCount} pending
                    </span>
                );
            default:
                return "—";
        }
    };

    if (loading && !arrayHasItems(sourceRows)) {
        return <LoaderSpinner colorSpinner="black" height="50" width="50" border="3" />;
    }

    return (
        <section className="mb-4 rounded-lg border border-border bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 border-b border-border pb-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <div className="mb-2 flex items-center gap-2">
                        <span className="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-primary shadow-none">
                            PO Schedule
                        </span>
                    </div>
                    <h2 className="text-xl font-semibold tracking-[-0.02em] text-primary">
                        Supplier purchase-order schedule
                    </h2>
                    <p className="mt-1 max-w-3xl text-sm text-gray-600">
                        Open To Buy drives the buy need. This view groups that demand by supplier, assigns it into
                        planned PO windows, and prioritizes the next relevant window from your selected As of Date.
                        For export, choose one supplier first so the file breaks out every SKU/product row and
                        purchasing can see exact material-buy counts.
                    </p>
                </div>
                <div className="flex flex-wrap items-end gap-2 flex-1 justify-end">
                    <label className="rounded-md border border-border bg-background px-3 py-2 text-sm w-full sm:w-auto">
                        <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">As of date</p>
                        <DatePickerInput
                            buttonClassName="!border-none !shadow-none !px-0 !py-0 !min-w-[140px]"
                            className="mt-1"
                            onChange={setAsOfDate}
                            placeholder="As of date"
                            value={asOfDate}
                        />
                    </label>
                    <label className="w-full sm:max-w-[230px] rounded-md border border-border bg-background px-3 py-2 text-sm">
                        <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">Export supplier</p>
                        <SelectDropdown
                            dropdownClassname="!z-[150]"
                            onChange={setExportSupplier}
                            options={exportSupplierOptions}
                            placeholder="Select supplier"
                            selectClassname="mt-1 !w-full sm:!w-auto !border-0 !bg-white !px-0 !py-0 !shadow-none"
                            selectedValueClassname="!text-sm !font-medium !text-primary !max-w-[180px]"
                            value={exportSupplier}
                        />
                    </label>
                    <button
                        className="inline-flex items-center w-full sm:w-auto rounded-md border border-border bg-white px-1 py-1 sm:py-2 sm:px-3 text-sm font-medium text-primary"
                        onClick={resetScheduleState}
                        type="button"
                    >
                        <RotateCcw className="mr-2 size-4" />
                        Reset schedule state
                    </button>
                    <button
                        className="inline-flex items-center w-full sm:w-auto rounded-md border border-border bg-white px-1 py-1 sm:py-2 sm:px-3 text-sm font-medium text-primary disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={!exportSupplier || exportRows.length === 0}
                        onClick={() => {
                            if (!exportSupplier || exportRows.length === 0) {
                                toast.error("Choose a supplier with SKU rows before exporting the PO schedule.");
                                return;
                            }
                            downloadPoScheduleCsv(exportRows, exportSupplier);
                        }}
                        type="button"
                    >
                        <Download className="mr-2 size-4" />
                        Export SKU Detail
                    </button>
                </div>
            </div>

            <TotalsPoSchedule totals={totals} />

            <div className="mt-3 flex flex-col gap-3 rounded-lg border border-border bg-background/60 px-3 py-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">Schedule focus</p>
                    <p className="mt-1 text-sm font-semibold text-primary">
                        {nextWindowDate
                            ? `Next upcoming PO window: ${formatPoScheduleDate(nextWindowDate)}`
                            : "No upcoming PO window exists after the selected As of Date."}
                    </p>
                    <p className="mt-1 max-w-3xl text-xs text-gray-600 whitespace-break-spaces">
                        Rows are ordered from the next upcoming buy window first. Use Reset schedule state to restore
                        the default company cadence and clear supplier overrides.
                    </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                    <POScheduleInformationBadge
                        label="Next window batches"
                        value={formatPoScheduleNumber(nextWindowBatchCount)}
                    />
                    <POScheduleInformationBadge
                        label="Elapsed windows"
                        value={formatPoScheduleNumber(elapsedWindowCount)}
                    />
                    <POScheduleInformationBadge
                        label="Saved overrides"
                        value={formatPoScheduleNumber(overrideCount)}
                    />
                </div>
            </div>

            <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(320px,0.82fr)_minmax(0,1.18fr)] xl:items-start">
                <aside
                    ref={scheduleAsideRef}
                    className="rounded-lg border border-border bg-background p-2 sm:p-4"
                >
                    <div className="border-b border-border pb-4">
                        <h3 className="text-sm font-semibold text-primary">Default PO windows</h3>
                        <p className="mt-1 text-xs text-gray-600 whitespace-break-spaces">
                            These are the default company-wide buy dates. Supplier overrides can change cadence or set
                            their own window dates below.
                        </p>
                        <div className="mt-3 grid gap-2">
                            {globalScheduleDates.map((date, index) => (
                                <label
                                    key={`global-date-${index}`}
                                    className="rounded-md border border-border bg-white px-1 py-1 sm:py-2 sm:px-3 text-sm"
                                >
                                    <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                        Default window {index + 1}
                                    </p>
                                    <DatePickerInput
                                        buttonClassName="!border-none !shadow-none !px-0 !py-0 !w-auto"
                                        className="mt-1 w-full"
                                        onChange={(value) => setGlobalScheduleDate(index, value)}
                                        placeholder={`Default window ${index + 1}`}
                                        value={date}
                                    />
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="mt-4 space-y-3">
                        <div>
                            <h3 className="text-sm font-semibold text-primary">Supplier cadence overrides</h3>
                            <p className="mt-1 text-xs text-gray-600 whitespace-break-spaces">
                                Use the global March, June, and December rhythm by default, or give individual suppliers
                                a denser cadence with their own editable PO dates.
                            </p>
                        </div>
                        {supplierSummaries.length > 0 ? (
                            supplierSummaries.map((summaryRow) => {
                                const hasOverride = Boolean(supplierOverrides[summaryRow.supplier]);
                                const selectedValue = effectiveCadenceValue(
                                    supplierOverrides[summaryRow.supplier]?.frequency
                                );
                                return (
                                    <div
                                        key={summaryRow.supplier}
                                        className="rounded-md border border-border bg-white px-1 py-1 sm:py-2 sm:px-3"
                                    >
                                        <div className="flex flex-col gap-3">
                                            <div className="flex flex-wrap items-start justify-between gap-3 sm:flex-row flex-col">
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-medium text-primary whitespace-break-spaces">
                                                        {summaryRow.supplier}
                                                    </p>
                                                    <p className="mt-1 text-xs text-gray-600 whitespace-break-spaces">
                                                        {summaryRow.batchCount} batch
                                                        {summaryRow.batchCount === 1 ? "" : "es"} ·{" "}
                                                        {formatPoScheduleNumber(summaryRow.skuCount)} SKUs ·{" "}
                                                        {formatPoScheduleCurrency(summaryRow.totalCash)}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <SelectDropdown
                                                        dropdownClassname="!z-[150]"
                                                        onChange={(value) => {
                                                            if (value === "global") {
                                                                clearSupplierOverride(summaryRow.supplier);
                                                                return;
                                                            }
                                                            setSupplierFrequency(summaryRow.supplier, Number(value));
                                                        }}
                                                        options={CADENCE_OPTIONS}
                                                        selectClassname="!min-w-[180px] !h-9 !rounded-md !border !border-border !bg-white !px-2 !py-0 !text-xs !text-primary"
                                                        value={selectedValue}
                                                    />
                                                    {hasOverride ? (
                                                        <button
                                                            className="inline-flex h-9 items-center rounded-md border border-border bg-white px-3 text-xs font-medium text-primary"
                                                            onClick={() => clearSupplierOverride(summaryRow.supplier)}
                                                            type="button"
                                                        >
                                                            <RotateCcw className="mr-2 size-3.5" />
                                                            Reset
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </div>

                                            <div className="grid gap-2 xl:grid-cols-2">
                                                {summaryRow.scheduleDates.map((date, index) => (
                                                    <label
                                                        key={`${summaryRow.supplier}-${index}`}
                                                        className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                                                    >
                                                        <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                                            {summaryRow.cadenceLabel} date {index + 1}
                                                        </p>
                                                        <DatePickerInput
                                                            buttonClassName={`!border-none !shadow-none !px-0 !py-0 !w-auto ${
                                                                !hasOverride ? "!cursor-not-allowed !opacity-60" : ""
                                                            }`}
                                                            className="mt-1 w-auto"
                                                            disabled={!hasOverride}
                                                            onChange={(value) =>
                                                                setSupplierScheduleDate(
                                                                    summaryRow.supplier,
                                                                    index,
                                                                    value
                                                                )
                                                            }
                                                            placeholder={`${summaryRow.cadenceLabel} date ${index + 1}`}
                                                            value={date}
                                                        />
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="rounded-md border border-dashed border-border bg-white px-4 py-6 text-sm text-gray-600 whitespace-break-spaces">
                                No supplier buy rows are currently in scope. Adjust your filters or review Open To Buy
                                rows with positive units to order.
                            </div>
                        )}
                    </div>
                </aside>

                <div
                    className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-background p-2 sm:p-4"
                    style={scheduleAsideHeight ? { height: scheduleAsideHeight } : undefined}
                >
                    <div className="flex shrink-0 flex-col gap-1 border-b border-border pb-4">
                        <h3 className="text-sm font-semibold text-primary">Scheduled PO batches</h3>
                        <p className="text-xs text-gray-600 whitespace-break-spaces">
                            Each row is a supplier batch created from Open To Buy demand. Planned rows fit the configured
                            cadence. The table is ranked from the next relevant upcoming batch based on the As of Date,
                            with older windows falling below it.
                        </p>
                    </div>

                    <div
                        className={`mt-4 min-h-0 overflow-auto rounded-md border border-border bg-white ${
                            scheduleAsideHeight ? "flex-1" : "max-h-[min(65vh,720px)]"
                        }`}
                    >
                        <table className="w-max min-w-full border-collapse text-sm">
                                <thead className="text-left">
                                    <tr className="border-b border-border">
                                        {columnIds.map((columnId) => (
                                            <th
                                                key={columnId}
                                                className="sticky top-0 z-20 bg-white px-3 py-3 font-medium text-gray-600"
                                            >
                                                {getDemandPlanningColumnLabel(columnId)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {orderedScheduleLines.length > 0 ? (
                                        orderedScheduleLines.map((line) => {
                                            const isNextWindow =
                                                Boolean(nextWindowDate) &&
                                                line.scheduledPoDate === nextWindowDate;
                                            const isPastWindow =
                                                (parseIsoDate(line.scheduledPoDate)?.getTime() ?? 0) < asOfTime;

                                            return (
                                                <tr
                                                    key={`${line.supplier}-${line.scheduledPoDate}-${line.scheduleType}`}
                                                    className={`group ${
                                                        isNextWindow ? "bg-slate-50/70" : ""
                                                    } ${isPastWindow ? "bg-background/40" : ""}`}
                                                >
                                                    {columnIds.map((columnId) => (
                                                        <td
                                                            key={`${line.supplier}-${line.scheduledPoDate}-${columnId}`}
                                                            className="border-b border-transparent px-3 py-3 text-primary group-hover:border-border"
                                                        >
                                                            {renderScheduleCell(columnId, line)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td
                                                className="px-4 py-8 text-sm text-gray-600 whitespace-break-spaces"
                                                colSpan={Math.max(columnIds.length, 1)}
                                            >
                                                No PO schedule rows are currently available from the filtered Open To
                                                Buy dataset.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default POScheduleTable;
